import { useMemo, useState } from "react";

import noImage from "../../../../assets/img/no_image.jpg";
import PlatformSupportIcons from "../../../../components/PlatformSupportIcons/PlatformSupportIcons";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { REACT_APP_API_URL } from "../../../../config/config";
import { useSiteData } from "../../../../contexts/SiteDataContext";
import { useLocale } from "../../../../i18n/LocaleContext";
import { useLocalized } from "../../../../i18n/localized";
import type { MessageKey } from "../../../../i18n/types";
import type { RatingCategory, Vpn } from "../../../../types";
import { parseMirrorUrl, pickRegionalPromo } from "../../../../utils/vpnUtils";
import RatingPageVpnCardDetails from "../../RatingPageVpnCardDetails/RatingPageVpnCardDetails";
import RatingPageVpnCardHeader from "./RatingPageVpnCardHeader";

interface RatingPageVpnCardProps {
  vpn: Vpn;
  index: number;
  rating: RatingCategory;
}

/** Operator placeholder: the code is already in the referral link, not a code to copy. */
const PROMO_ALREADY_ON_BUTTON = "уже на кнопке";

function localizePromoLabel(
  value: string | undefined,
  translate: (key: MessageKey) => string,
): string {
  if ((value ?? "").trim().toLowerCase() === PROMO_ALREADY_ON_BUTTON) {
    return translate("vpn.promoOnButton");
  }
  return value ?? "";
}

function RatingPageVpnCard({ vpn, index, rating }: RatingPageVpnCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { siteData } = useSiteData();
  const { locale, t } = useLocale();
  const text = useLocalized();

  const officialWebsite = useMemo(() => {
    if (rating.name === "Прочие VPN") {
      return "";
    }

    const regionCode = siteData?.region?.code;

    switch (regionCode) {
      case "ru":
        return vpn.official_website;
      case "kz":
        return vpn.official_website_kz || vpn.official_website;
      case "az":
        return vpn.official_website_az || vpn.official_website;
      default:
        return vpn.official_website;
    }
  }, [rating.name, vpn, siteData]);

  const secretWord = useMemo(() => {
    const stored = pickRegionalPromo(
      locale,
      siteData?.region?.code,
      vpn.secret_word,
      vpn.secret_word_kz,
      vpn.secret_word_az,
      vpn.secret_word_en,
    );

    return localizePromoLabel(stored, t);
  }, [vpn, siteData, locale, t]);

  // The mirror is a RU-specific alternative, so it is offered only there —
  // KZ/AZ have their own official_website_* fields.
  const alternativeUrl = useMemo(() => {
    const regionCode = siteData?.region?.code;

    if (regionCode && regionCode !== "ru") {
      return "";
    }

    return parseMirrorUrl(vpn.website_accessible_from_ru);
  }, [vpn, siteData]);

  const hintPromocode = useMemo(
    () =>
      pickRegionalPromo(
        locale,
        siteData?.region?.code,
        vpn.hint_promocode,
        vpn.hint_promocode_kz,
        vpn.hint_promocode_az,
        vpn.hint_promocode_en,
      ),
    [vpn, siteData, locale],
  );

  return (
    <div
      key={index}
      className="cards flex text-center flex-col md:flex-row mb-3 inline-block w-full bg-light-blue border border-solid border-light-blue-2 rounded-2xl"
    >
      <div className="hidden lg:flex shrink-0 w-[15.5rem] max-h-[12.875rem] justify-center items-center p-3">
        <img
          src={vpn.logo ? REACT_APP_API_URL + vpn.logo : noImage}
          onError={(e) => {
            e.currentTarget.src = noImage;
          }}
          alt={vpn.name}
          className="w-full h-full object-contain rounded-xl bg-black-0f"
        />
      </div>

      {/* min-w-0 is load-bearing: a flex item defaults to min-width:auto, so
          one long unbreakable string in the comment (VPN Unlimited ships a
          216-char URL) sets a huge min-content width, blows this column out
          and pushes the CTA column outside the card. overflow-hidden on the
          spoiler does not help — it clips paint, not intrinsic width. */}
      <div className="flex-1 min-w-0 border-light md:border-r border-solid p-3 pb-0 md:p-6 md:pr-[4rem] md:pl-[4rem]">
        <RatingPageVpnCardHeader
          vpn={vpn}
          rating={rating}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />

        <RatingPageVpnCardDetails vpn={vpn} isOpen={isOpen} />
      </div>

      <div className="min-w-0 max-w-full md:w-[13.5rem] md:shrink-0 text-left md:text-center flex flex-row md:flex-col justify-center p-3 md:p-[1.375rem] md:border-t-0 border-t border-solid border-gray-4">
        {vpn.rating_position && (
          <div className="min-w-0 md:mb-5 flex-1 md:flex-none">
            <p className="text-xs font-normal md:mb-1 break-words">
              {text(siteData.rating_position, "title")}
            </p>
            <p className="font-extrabold text-lg break-words">
              {vpn.rating_position}
            </p>
          </div>
        )}
        {secretWord && (
          <div className="min-w-0 md:mb-5 flex-1 md:flex-none md:text-center">
            <p className="text-xs font-normal md:mb-1 break-words">
              {text(siteData.get_a_bonus, "title")}
            </p>
            <div className="font-extrabold text-lg leading-snug break-words">
              {secretWord}

              {hintPromocode && (
                <Tooltip
                  text={hintPromocode}
                  subContainerClassName="w-full h-full items-center justify-center flex"
                  className="ml-1 w-[1.3rem] h-[1.3rem] inline-flex items-center justify-center text-center cursor-pointer"
                  disablePad
                >
                  <span
                    className={`text-xs-x font-semibold rounded-full cursor-pointer text-white leading-none w-[0.9rem] h-[0.9rem] items-center justify-center flex bg-yellow italic`}
                  >
                    i
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col justify-end md:w-full flex-1 md:flex-none">
          <div className="flex justify-end md:w-full">
            <a
              href={officialWebsite || undefined}
              target={officialWebsite ? "_blank" : undefined}
              rel={officialWebsite ? "noreferrer" : undefined}
              data-umami-event="VPN Service Click"
              data-umami-event-vpn-name={vpn.name}
              onClick={(e) => {
                if (!officialWebsite) {
                  e.preventDefault();
                }
              }}
              className={`button block md:inline-block bg-yellow bg-transparent text-white text-sm font-extrabold leading-0 rounded-lg text-center py-2 sm:py-3 px-2 sm:px-6 justify-center flex items-center ${
                !officialWebsite ? "opacity-50" : ""
              }`}
            >
              {t("vpn.goToService")}
            </a>
          </div>

          {alternativeUrl && (
            <a
              href={alternativeUrl}
              target="_blank"
              rel="noreferrer"
              data-umami-event="VPN Alternative Link Click"
              data-umami-event-vpn-name={vpn.name}
              className="text-link-blue text-xs underline mt-2 text-right md:text-center"
            >
              {t("vpn.altLink")}
            </a>
          )}
        </div>

        <PlatformSupportIcons
          vpn={vpn}
          className="hidden md:block w-full mt-5"
        />
      </div>
    </div>
  );
}

export default RatingPageVpnCard;
